import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Search, Sparkles, ShieldCheck } from "lucide-react";

import { usePageMeta } from "../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /research , the A-Z index page that links to every per-condition
 * SEO landing page. It exists for two reasons:
 *
 *   1) Internal-linking: every condition page is one hop from the
 *      footer, which gives Google a clean crawl path and accelerates
 *      indexing of all 700+ pages.
 *   2) UX: lets a patient browse the entire condition library if they
 *      can't remember the exact name or aren't sure if we cover them.
 *
 * The index also flags "X open" pills next to conditions that currently
 * have at least one live MediTalk study , the most actionable signal
 * on this page.
 */
export default function ResearchIndexPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/research`);
        if (!cancelled) setData(data);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    if (!data) return null;
    const q = query.trim().toLowerCase();
    if (!q) return data.letters;
    const out = [];
    for (const L of data.letters) {
      const items = L.items.filter(
        (it) =>
          it.label.toLowerCase().includes(q) ||
          it.short.toLowerCase().includes(q),
      );
      if (items.length) out.push({ ...L, items });
    }
    return out;
  }, [data, query]);

  const totalMatches = useMemo(
    () => (filtered ? filtered.reduce((n, L) => n + L.items.length, 0) : 0),
    [filtered],
  );

  // JSON-LD for the index page itself , one CollectionPage node + a
  // BreadcrumbList. Google uses CollectionPage to understand that this
  // is a hub page that lists many things (each "thing" is a condition).
  const jsonLd = data
    ? [
        {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "All MediTalk research conditions",
          url: "https://www.meditalk.world/research",
          description:
            `Browse every condition MediTalk currently runs paid healthcare research for , ${data.total} conditions across patient and caregiver studies.`,
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://www.meditalk.world/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Research",
              item: "https://www.meditalk.world/research",
            },
          ],
        },
      ]
    : null;

  usePageMeta({
    path: "/research",
    title: "Browse MediTalk research conditions , A-Z",
    description: data
      ? `Browse paid healthcare research opportunities across ${data.total} conditions , from asthma to ulcerative colitis. ${data.total_open_studies} ${data.total_open_studies === 1 ? "study" : "studies"} currently recruiting.`
      : "Browse paid healthcare research opportunities by condition.",
    jsonLd,
  });

  if (loading) {
    return (
      <div className="min-h-screen">
        <section className="bg-[var(--dark-bg)] py-16">
          <div className="container-main">
            <div className="h-10 w-2/3 rounded bg-white/10 mb-3 animate-pulse" />
            <div className="h-5 w-1/2 rounded bg-white/10 animate-pulse" />
          </div>
        </section>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen">
        <section className="bg-[var(--dark-bg)] py-20">
          <div className="container-main text-center">
            <h1 className="text-3xl md:text-4xl font-['Outfit'] font-bold text-white mb-3">
              Couldn't load the condition list
            </h1>
            <p className="text-[var(--gray-light)] mb-8">
              Please try again in a moment.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--lime)] text-[var(--dark-bg)] font-bold hover:bg-[var(--lime-dark)] transition-colors"
            >
              Register interest
              <ArrowRight size={16} />
            </Link>
          </div>
        </section>
      </div>
    );
  }

  const lettersList = data.letters.map((L) => L.letter);

  return (
    <div className="min-h-screen" data-testid="research-index-page">
      {/* HERO , DARK ------------------------------------------------- */}
      <section className="bg-[var(--dark-bg)] py-14 md:py-20">
        <div className="container-main">
          <nav
            className="flex items-center gap-2 text-xs text-[var(--gray-light)] mb-5"
            aria-label="Breadcrumb"
          >
            <Link to="/" className="hover:text-white">
              Home
            </Link>
            <span aria-hidden="true">›</span>
            <span className="text-white">Research</span>
          </nav>
          <span className="badge badge-terracotta mb-4">All conditions</span>
          <h1
            className="text-3xl md:text-5xl font-['Outfit'] font-bold tracking-tight text-white mb-4 max-w-3xl"
            data-testid="research-index-h1"
          >
            Browse paid research by condition
          </h1>
          <p className="text-[var(--gray-light)] max-w-2xl text-base md:text-lg leading-relaxed mb-6">
            MediTalk runs paid healthcare research with patients and caregivers
            across <strong className="text-white">{data.total}</strong>{" "}
            conditions.{" "}
            {data.total_open_studies > 0 ? (
              <>
                <strong className="text-[var(--lime)]">
                  {data.total_open_studies}
                </strong>{" "}
                {data.total_open_studies === 1 ? "study is" : "studies are"}{" "}
                currently recruiting. Tap any condition below to see what's open
                or register your interest.
              </>
            ) : (
              "No studies are currently recruiting. Tap any condition below to register your interest , we'll email you the moment a study opens."
            )}
          </p>

          {/* Inline search field */}
          <div className="relative max-w-md">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search conditions (e.g. migraine)…"
              className="w-full pl-10 pr-3 py-3 rounded-full border-0 bg-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[var(--lime)]"
              data-testid="research-index-search"
              aria-label="Search conditions"
            />
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3 text-sm text-white/80">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10">
              <ShieldCheck size={14} className="text-[var(--lime)]" />
              GDPR safe
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10">
              <Sparkles size={14} className="text-[var(--lime)]" />
              Compensated participation
            </span>
          </div>
        </div>
      </section>

      {/* ALPHABET JUMP STRIP ---------------------------------------- */}
      <section className="bg-white border-b border-[var(--light-border)] sticky top-0 z-20">
        <div className="container-main py-3 flex flex-wrap items-center gap-1.5">
          {lettersList.map((L) => (
            <a
              key={L}
              href={`#letter-${L}`}
              className="inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold text-[var(--charcoal)] hover:bg-[var(--lime)] hover:text-[var(--dark-bg)] transition-colors"
              data-testid={`jump-${L}`}
            >
              {L}
            </a>
          ))}
        </div>
      </section>

      {/* A-Z GRID --------------------------------------------------- */}
      <section className="py-10 md:py-14 bg-white">
        <div className="container-main">
          {query.trim() && (
            <p
              className="text-sm text-[var(--text-muted)] mb-6"
              data-testid="search-summary"
            >
              {totalMatches}{" "}
              {totalMatches === 1 ? "condition matches" : "conditions match"} "
              {query.trim()}"
            </p>
          )}

          {totalMatches === 0 ? (
            <div className="rounded-2xl border border-dashed border-[var(--light-border)] bg-[var(--bone-soft)] p-8 md:p-10 text-center">
              <p className="text-base text-[var(--charcoal)] font-semibold mb-1">
                No conditions match "{query.trim()}"
              </p>
              <p className="text-sm text-[var(--text-muted)] mb-5 max-w-xl mx-auto">
                You can still register your interest. Use the "Other (specify)"
                field on the form to tell us about your condition.
              </p>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[var(--lime)] text-[var(--dark-bg)] font-bold hover:bg-[var(--lime-dark)] transition-colors"
                data-testid="research-index-noresults-register"
              >
                Register interest
                <ArrowRight size={16} />
              </Link>
            </div>
          ) : (
            <div className="space-y-10">
              {filtered.map((L) => (
                <section
                  key={L.letter}
                  id={`letter-${L.letter}`}
                  className="scroll-mt-24"
                  data-testid={`letter-section-${L.letter}`}
                >
                  <h2 className="text-2xl md:text-3xl font-['Outfit'] font-bold text-[var(--navy)] mb-4 pb-2 border-b border-[var(--light-border)]">
                    {L.letter}
                  </h2>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2">
                    {L.items.map((it) => (
                      <li key={it.slug}>
                        <Link
                          to={`/research/${it.slug}`}
                          className="group flex items-center justify-between gap-2 py-2 px-3 -mx-3 rounded-lg hover:bg-[var(--bone-soft)] transition-colors"
                          data-testid={`condition-link-${it.slug}`}
                        >
                          <span className="text-sm text-[var(--charcoal)] group-hover:text-[var(--navy)] truncate">
                            {it.short}
                          </span>
                          {it.open_count > 0 && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full bg-[var(--lime)] text-[var(--dark-bg)] text-[10px] font-bold flex-shrink-0"
                              title={`${it.open_count} open ${
                                it.open_count === 1 ? "study" : "studies"
                              }`}
                            >
                              {it.open_count} open
                            </span>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CLOSING CTA , DARK ---------------------------------------- */}
      <section className="py-14 md:py-20 bg-[var(--dark-bg)]">
        <div className="container-main text-center max-w-2xl">
          <h2 className="text-2xl md:text-4xl font-['Outfit'] font-bold text-white mb-4">
            Can't find your condition?
          </h2>
          <p className="text-[var(--gray-light)] mb-8 leading-relaxed">
            Register your interest anyway , use the "Other (specify)" field on
            the form and we'll match you to studies as soon as one opens.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--lime)] text-[var(--dark-bg)] font-bold hover:bg-[var(--lime-dark)] transition-colors"
            data-testid="research-index-final-register"
          >
            Register interest
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
